import React from 'react';
import { Check, Circle } from 'lucide-react';

/* Password strength scoring + meter for registration flows.
   Plain-language, calm visuals: a requirements checklist (hard minimum)
   plus a 4-segment strength meter (quality beyond the minimum).
   Semantic color only on the filled segments + met checks. */

const COMMON_PASSWORDS = new Set([
  'password', 'password1', 'password123', '12345678', '123456789', '1234567890',
  'qwerty123', 'qwertyuiop', 'iloveyou', 'admin123', 'welcome1', 'letmein1',
  'monkey123', 'dragon123', 'sunshine1', 'princess1', 'football1', 'baseball1',
  'master123', 'shadow123', 'superman1', 'michael1', 'jennifer1', 'million2',
  'aaaaaaaa', '11111111', 'abc12345', 'password!', 'p@ssw0rd', 'passw0rd1',
]);

export interface PasswordRequirement {
  label: string;
  met: boolean;
}

export interface PasswordScore {
  /** 0–4. 0–1 weak, 2 okay, 3 good, 4 strong. */
  score: number;
  label: string;
  /** The single most useful next improvement, or null when strong. */
  hint: string | null;
  /** The hard minimum rules and whether each is met. */
  requirements: PasswordRequirement[];
  /** True only when every hard requirement is met (and not a known-weak password). */
  acceptable: boolean;
}

export function scorePassword(password: string, email?: string, username?: string): PasswordScore {
  const pw = password || '';
  const lower = pw.toLowerCase();

  const hasLower = /[a-z]/.test(pw);
  const hasUpper = /[A-Z]/.test(pw);
  const hasDigit = /\d/.test(pw);
  const hasSymbol = /[^A-Za-z0-9]/.test(pw);
  const longEnough = pw.length >= 8;
  const isLong = pw.length >= 12;

  const emailLocal = (email || '').split('@')[0]?.toLowerCase();
  const containsIdentity =
    (!!username && username.length >= 3 && lower.includes(username.toLowerCase())) ||
    (!!emailLocal && emailLocal.length >= 3 && lower.includes(emailLocal));
  const isCommon = COMMON_PASSWORDS.has(lower);
  const isRepetitive = pw.length > 0 && /^(.)\1*$/.test(pw);

  // Hard minimum — must all be met to register.
  const requirements: PasswordRequirement[] = [
    { label: 'At least 8 characters', met: longEnough },
    { label: 'An uppercase letter (A–Z)', met: hasUpper },
    { label: 'A lowercase letter (a–z)', met: hasLower },
    { label: 'A number (0–9)', met: hasDigit },
  ];
  const meetsAll = requirements.every((r) => r.met);

  // Strength score (quality beyond the minimum).
  let score = 0;
  if (longEnough) score++;
  if (isLong) score++;
  if (hasLower && hasUpper) score++;
  if (hasDigit) score++;
  if (hasSymbol) score++;
  score = Math.min(score, 4);
  if (isCommon || isRepetitive) score = Math.min(score, 1);
  if (containsIdentity) score = Math.min(score, 2);

  let hint: string | null = null;
  if (pw.length === 0) hint = null;
  else if (isCommon) hint = 'This password is too easy to guess — try something more personal to you';
  else if (isRepetitive) hint = 'Avoid repeating the same character';
  else if (containsIdentity) hint = 'Avoid using your name or email in the password';
  else if (meetsAll && !hasSymbol) hint = 'Add a symbol like ! or # to make it stronger';
  else if (meetsAll && !isLong) hint = 'Looking good — 12+ characters is even stronger';

  const labels = ['Weak', 'Weak', 'Okay', 'Good', 'Strong'];

  return {
    score,
    label: labels[score],
    hint,
    requirements,
    acceptable: meetsAll && !isCommon && !isRepetitive,
  };
}

const SEGMENT_COLORS = ['#ef4444', '#ef4444', '#f59e0b', '#22c55e', '#22c55e'];
const LABEL_COLORS_LIGHT = ['#b91c1c', '#b91c1c', '#b45309', '#15803d', '#15803d'];

const PasswordStrength: React.FC<{
  password: string;
  email?: string;
  username?: string;
}> = ({ password, email, username }) => {
  if (!password) return null;

  const result = scorePassword(password, email, username);
  const color = SEGMENT_COLORS[result.score];

  return (
    <div aria-live="polite" className="mt-2">
      {/* Strength meter */}
      <div className="flex items-center gap-2">
        <div className="flex flex-1 gap-1">
          {[1, 2, 3, 4].map((seg) => (
            <div
              key={seg}
              className="h-1 flex-1 rounded-full transition-colors duration-200"
              style={{
                backgroundColor: seg <= result.score ? color : 'rgba(128, 120, 118, 0.25)',
              }}
            />
          ))}
        </div>
        <span
          className="text-xs font-medium w-12 text-right dark:brightness-125"
          style={{ color: LABEL_COLORS_LIGHT[result.score] }}
        >
          {result.label}
        </span>
      </div>

      {/* Requirements checklist */}
      <ul className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1">
        {result.requirements.map((req) => (
          <li key={req.label} className="flex items-center gap-1.5 text-xs">
            {req.met ? (
              <Check className="w-3.5 h-3.5 shrink-0 text-green-600 dark:text-green-400" />
            ) : (
              <Circle className="w-3 h-3 shrink-0 text-foreground-subtle" />
            )}
            <span className={req.met ? 'text-foreground-secondary' : 'text-foreground-muted'}>
              {req.label}
            </span>
          </li>
        ))}
      </ul>

      {result.hint && (
        <p className="text-xs text-foreground-muted mt-1.5">{result.hint}</p>
      )}
    </div>
  );
};

export default PasswordStrength;
